import { lazy, Suspense, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber } from "./numberInput";
import { checkBody, goalTargets, MIN_CALORIES } from "./calorie";
import { BMR_FORMULAS, bmrByFormula, bmrSteps, bodyFatError, compareFormulas, resolveFormula, type FormulaChoice, type FormulaRow } from "./bmrFormulas";
import { reportTable } from "@/lib/calc/report";
import type { TableColumn } from "@/lib/calc/table";

const CalcReport = lazy(() => import("@/components/calc/CalcReport"));
import { NoticeBox } from "@/components/NoticeBox";
import { MACRO_GOALS } from "./macroSplit";

const GOALS = MACRO_GOALS;

const TOOL_INFO_CONTENT = {
  what: `Calculates your Basal Metabolic Rate (the calories your body burns at rest) and suggests a macronutrient breakdown (protein, carbs, fat) based on your goals.`,
  how: `Enter your age, sex, height, weight, and activity level, and your estimated BMR, total daily calorie needs, and suggested macro split are calculated. The default equation is Mifflin-St Jeor. If you know your body fat percentage, enter it to use Katch-McArdle, or pick Harris-Benedict from the formula list.`,
  tip: `Activity level is the input people most commonly overestimate — being honest about how active you actually are (not how active you'd like to be) gives a meaningfully more accurate calorie estimate.`,
};

export default function BmrMacroCalculator() {
  const [gender, setGender] = useState<"male" | "female">("male");
  const [age, setAge] = useState("30");
  const [heightCm, setHeightCm] = useState("175");
  const [weightKg, setWeightKg] = useState("75");
  const [activity, setActivity] = useState("1.375");
  const [goalId, setGoalId] = useState("maintain");
  const [bodyFat, setBodyFat] = useState("");
  const [choice, setChoice] = useState<FormulaChoice>("auto");

  const a = parseNumber(age) ?? 0;
  const h = parseNumber(heightCm) ?? 0;
  const w = parseNumber(weightKg) ?? 0;
  const check = checkBody(a, h, w);

  const bfText = bodyFat.trim();
  const bfNum = bfText === "" ? undefined : parseNumber(bodyFat) ?? NaN;
  const bfErr = bfNum === undefined ? "" : bodyFatError(bfNum);
  const bfUsable = bfNum !== undefined && !bfErr ? bfNum : undefined;
  const resolved = resolveFormula(choice, bfUsable);
  const bmrInput = { sex: gender, weightKg: w, heightCm: h, age: a, bodyFatPct: bfUsable };
  const bmr = bmrByFormula(resolved.formula, bmrInput) ?? 0;
  const tdee = bmr * (Number(activity) || 1.2);
  const goal = GOALS.find(g => g.id === goalId) ?? GOALS[1];
  const targets = check.error ? [] : goalTargets(tdee, gender, check.underweight);
  const lossT = targets[1];
  const calorieTarget = goalId === "lose" ? (lossT && !lossT.unavailable ? lossT.kcal : tdee) : goalId === "gain" ? tdee + 300 : tdee;
  const lossBlocked = goalId === "lose" && !!lossT && lossT.unavailable;
  const lossClamped = goalId === "lose" && !!lossT && lossT.clamped;

  const proteinG = (calorieTarget * goal.proteinPct) / 4;
  const carbG = (calorieTarget * goal.carbPct) / 4;
  const fatG = (calorieTarget * goal.fatPct) / 9;

  const { blocked, gate } = useLogToolUsage(true, 3000);
  const actFactor = Number(activity) || 1.2;
  const info = BMR_FORMULAS[resolved.formula];
  const rows = useMemo(() => (check.error ? [] : compareFormulas(bmrInput)), [check.error, gender, w, h, a, bfUsable]); // eslint-disable-line react-hooks/exhaustive-deps
  const steps = useMemo(() => (check.error ? [] : bmrSteps(resolved.formula, bmrInput, actFactor, (n) => Math.round(n * 10) / 10)), [check.error, resolved.formula, gender, w, h, a, bfUsable, actFactor]); // eslint-disable-line react-hooks/exhaustive-deps
  const columns: TableColumn<FormulaRow>[] = [
    { key: "formula", header: "Formula", get: (r) => (r.formula === resolved.formula ? `${r.name} (used)` : r.name) },
    { key: "bmr", header: "BMR (cal/day)", get: (r) => Math.round(r.bmr ?? 0), format: (r) => Math.round(r.bmr ?? 0).toLocaleString(), align: "right" },
    { key: "tdee", header: "With activity (cal/day)", get: (r) => Math.round((r.bmr ?? 0) * actFactor), format: (r) => Math.round((r.bmr ?? 0) * actFactor).toLocaleString(), align: "right" },
    { key: "equation", header: "Equation", get: (r) => r.equation },
  ];


  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <Tabs value={gender} onValueChange={v => setGender(v as "male" | "female")}>
          <TabsList className="w-full">
            <TabsTrigger value="male" className="flex-1">Male</TabsTrigger>
            <TabsTrigger value="female" className="flex-1">Female</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-3 gap-3">
          <div><Label>Age</Label><Input inputMode="decimal" aria-label="Age" value={age} onChange={e => setAge(e.target.value)} /></div>
          <div><Label>Height (cm)</Label><Input inputMode="decimal" aria-label="Height (cm)" value={heightCm} onChange={e => setHeightCm(e.target.value)} /></div>
          <div><Label>Weight (kg)</Label><Input inputMode="decimal" aria-label="Weight (kg)" value={weightKg} onChange={e => setWeightKg(e.target.value)} /></div>
        </div>

        <div>
          <Label>Activity Level</Label>
          <Select value={activity} onValueChange={setActivity}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1.2">Sedentary (little/no exercise)</SelectItem>
              <SelectItem value="1.375">Light exercise (1-3 days/week)</SelectItem>
              <SelectItem value="1.55">Moderate exercise (3-5 days/week)</SelectItem>
              <SelectItem value="1.725">Heavy exercise (6-7 days/week)</SelectItem>
              <SelectItem value="1.9">Athlete (2x/day training)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Body fat % (optional)</Label>
            <Input inputMode="decimal" aria-label="Body fat percent" value={bodyFat} onChange={e => setBodyFat(e.target.value)} aria-invalid={!!bfErr} />
            {bfErr ? <p role="alert" className="text-xs text-destructive mt-1" data-testid="bmr-bf-error">{bfErr}</p> : <p className="text-xs text-muted-foreground mt-1">From a scan or calipers. Switches to Katch-McArdle.</p>}
          </div>
          <div>
            <Label>Formula</Label>
            <Select value={choice} onValueChange={(v) => setChoice(v as FormulaChoice)}>
              <SelectTrigger aria-label="Formula"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automatic</SelectItem>
                <SelectItem value="mifflin">Mifflin-St Jeor</SelectItem>
                <SelectItem value="harris">Harris-Benedict (revised)</SelectItem>
                <SelectItem value="katch">Katch-McArdle (needs body fat)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Automatic: Mifflin-St Jeor, or Katch-McArdle when you enter body fat.</p>
          </div>
        </div>

        <div>
          <Label>Goal</Label>
          <Select value={goalId} onValueChange={setGoalId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {GOALS.map(g => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {check.error ? (
          <p className="text-sm text-red-600" data-testid="bmr-error">{check.error}</p>
        ) : blocked ? (
          <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
        ) : (
          <>
            {lossBlocked && <NoticeBox className="text-xs" testId="bmr-noloss">{check.underweight ? "Your BMI is under 18.5, so no weight-loss target is suggested." : `Your maintenance level is close to the safe minimum of ${MIN_CALORIES[gender]} cal, so no calorie deficit is suggested.`} Showing maintenance instead.</NoticeBox>}
            {lossClamped && <NoticeBox className="text-xs" testId="bmr-clamped">The loss target is limited to a safe level (never under {MIN_CALORIES[gender]} cal). Eating less should only be done under medical supervision.</NoticeBox>}
            {resolved.reason === "body-fat-needed" && <NoticeBox className="text-xs" testId="bmr-bf-needed">Katch-McArdle needs your body fat percentage. Showing Mifflin-St Jeor until you enter it.</NoticeBox>}
            <p className="text-xs text-center text-muted-foreground" data-testid="bmr-formula-used">Formula used: <strong>{info.name}</strong>{resolved.reason === "body-fat-entered" ? " (because you entered body fat)" : ""}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">BMR</p><p className="text-base font-bold">{Math.round(bmr)} cal</p></div>
              <div className="text-center p-3 rounded-lg border bg-primary/5"><p className="text-xs text-muted-foreground">Daily Calorie Target</p><p className="text-base font-bold text-primary" data-testid="bmr-target">{Math.round(calorieTarget)} cal</p></div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Protein</p><p className="text-sm font-bold">{Math.round(proteinG)}g</p></div>
              <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Carbs</p><p className="text-sm font-bold">{Math.round(carbG)}g</p></div>
              <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Fat</p><p className="text-sm font-bold">{Math.round(fatG)}g</p></div>
            </div>
            <p className="text-xs text-muted-foreground text-center" data-testid="bmr-macro-note">
              Macro split used: {Math.round(goal.proteinPct * 100)}% protein / {Math.round(goal.carbPct * 100)}% carbs / {Math.round(goal.fatPct * 100)}% fat of the calorie target (protein and carbs 4 cal per g, fat 9 cal per g). That is about {w > 0 ? (proteinG / w).toFixed(1) : "0"} g of protein per kg of body weight. The split is a fixed starting point for each goal, not a prescription.
            </p>
          </>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Based on the {info.name} equation ({info.citation}). An estimate — individual needs vary. Consult a doctor or dietitian for personalized guidance.
        </p>
        {!check.error && !blocked && (
          <Suspense fallback={<p className="text-xs text-muted-foreground" role="status">Loading working and export...</p>}>
            <CalcReport
              title="BMR calculation" tool="bmr-macro-calculator" gate={gate} filename="bmr.csv" steps={steps}
              summary={`BMR ${Math.round(bmr)} cal/day by ${info.name}; with activity ${Math.round(tdee)} cal/day`}
              notes={[`Formula used: ${info.name}, ${info.equation(gender)}. ${info.citation}.`, "An estimate of resting energy use in healthy adults. Not medical advice."]}
              tables={[reportTable({ caption: "BMR by formula", heading: "BMR by formula", rows, columns, rowKey: (r) => r.formula })]}
            />
          </Suspense>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
